// src/integrations/tradier/client.ts

import type {
  AccountResponse,
  Account,
  PositionResponse,
  Position,
  OrdersResponse,
  Order,
  QuotesResponse,
  Quote,
  UserProfileResponse,
  UserProfile,
  UserBalancesResponse,
  UserBalances
} from './types';

const BASE_URL = '/api/tradier'; // Changed to /api/tradier

function getHeaders(tokenOverride?: string) {
  const token = tokenOverride || import.meta.env.VITE_TRADIER_TOKEN;
  if (!token) throw new Error('VITE_TRADIER_TOKEN is not defined');
  return {
    Authorization: `Bearer ${token}`,
    Accept: 'application/json'
  };
}

async function request<T>(endpoint: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
  const fullPath = `${BASE_URL}${endpoint}`;
  const url = new URL(fullPath, window.location.origin); // Correctly use window.location.origin as base
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }
  const finalHeaders = headers || getHeaders(); // Use provided headers or generate new ones
  const response = await fetch(url.toString(), { headers: finalHeaders });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Tradier API error ${response.status}: ${text}`);
  }
  const data = await response.json();
  return data as T;
}

export async function getAccounts(token?: string): Promise<Account[]> {
  const headers = getHeaders(token);
  // This endpoint might not be used if profile directly gives accounts, or needs to be created if still needed.
  // For now, assuming profile is the source of accounts.
  console.warn("getAccounts is not fully implemented to use new backend structure, relies on profile data elsewhere.")
  return []; 
}

export async function getTradierPositions(token?: string): Promise<Record<string, Position[]>> {
  const headers = getHeaders(token);
  const data = await request<Record<string, Position[]>>('/positions', undefined, headers);
  return data;
}

export async function getOrders(accountId: string, token?: string): Promise<Order[]> {
  const headers = getHeaders(token);
  const data = await request<OrdersResponse>(`/accounts/${accountId}/orders`, undefined, headers);
  return data.orders.order;
}

export async function getQuotes(symbols: string[], token?: string): Promise<Quote[]> {
  const params = { symbols: symbols.join(',') };
  const headers = getHeaders(token);
  const data = await request<QuotesResponse>('/markets/quotes', params, headers);
  const quotes = Array.isArray(data.quotes.quote) ? data.quotes.quote : [data.quotes.quote];
  return quotes;
}

export async function getTradierUserProfile(token?: string): Promise<UserProfile> {
  const headers = getHeaders(token);
  const data = await request<UserProfile>('/profile', undefined, headers);
  return data; // The backend now returns the profile object directly
}

export async function getTradierUserBalances(token?: string): Promise<Record<string, UserBalances>> {
  const headers = getHeaders(token);
  const data = await request<Record<string, UserBalances>>('/balances', undefined, headers);
  return data;
}

// Default export for runtime compatibility
const tradierClient = { 
  getAccounts, 
  getPositions: getTradierPositions, // Ensure this uses the renamed/updated function
  getOrders, 
  getQuotes, 
  getUserProfile: getTradierUserProfile, // Ensure this uses the renamed/updated function
  getUserBalances: getTradierUserBalances, // Ensure this uses the renamed/updated function
};
export default tradierClient;

